import { useEffect, useRef, useState } from 'react';
import { Button, Input, InputRef, notification } from 'antd';
import { LeftSquareOutlined } from '@ant-design/icons';
import { KeyPad } from '@/components/KeyPad/KeyPad';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { Dynamics } from '@/services/dynamics/dynamics';
import answerIcon from '@/public/icons/answer.svg';
import { State } from '@/services/state.service';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import { SipJs } from '@/services/sip-js.service';
import './DialPad.scss';

const MAX_PHONE_LENGTH = 20;

export const DialPad = observer(() => {
  const {
    callStore: { setContacts, setActiveContact },
  } = useStore();
  const [notificationApi, contextHolder] = notification.useNotification();
  notification.config({ maxCount: 1 });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [inputCursorPosition, setInputCursorPosition] = useState({
    start: 0,
    end: 0,
  });
  const inputEl = useRef<InputRef>(null);

  useEffect(() => {
    const input = inputEl.current.input;
    const { start, end } = inputCursorPosition;

    input.setSelectionRange(start, end);
    input.focus();
  }, [inputCursorPosition]);

  const makeCall = async (phone: string) => {
    const { state } = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    if (state !== 'granted') {
      notificationApi.error({
        message: 'Microphone permission is required',
        placement: 'bottomRight',
        duration: 5,
        className: 'notifications',
      });

      return;
    }
    setContacts(null);
    if (SipJs.sessions?.length) {
      return;
    }

    if (!phone.trim()) {
      return;
    }

    const { isDialerActive } = State.state$.getValue();

    if (isDialerActive) {
      return;
    }
    commonMakeCall([], phone);
    const entities = await Dynamics.searchEntityByCalls({
      phone,
      isExactPhoneSearch: true,
    });
    if (entities?.length) {
      setContacts(entities);
      setActiveContact(entities[0]);
    }
  };

  const handleEnterPress = function (e) {
    if (e.key === 'Enter') {
      makeCall(phoneNumber);
    }
  };

  const addChar = (key: string) => {
    const input = inputEl.current.input;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const valueLength = input.value.length;

    if (valueLength === MAX_PHONE_LENGTH) {
      return;
    }

    if (valueLength === end && start === end) {
      setPhoneNumber((prevKey) => prevKey + key);
      setInputCursorPosition({ start: end + 1, end: end + 1 });
      return;
    }

    if (start === end) {
      setPhoneNumber((prevKey) => {
        return (
          prevKey.substring(0, start) +
          key +
          prevKey.substring(end, prevKey.length)
        );
      });
      setInputCursorPosition({ start: start + 1, end: end + 1 });
      return;
    }

    setPhoneNumber((prevKey) => {
      return (
        prevKey.substring(0, start) +
        key +
        prevKey.substring(end, prevKey.length)
      );
    });
    setInputCursorPosition({ start: start + 1, end: start + 1 });
  };

  const onChange = (e) => {
    const phone = e.target.value;
    const sipPhone = phone.replace(/[^0-9*#]/g, '');
    setPhoneNumber(sipPhone);
  };

  const removeKey = () => {
    const start = inputEl.current.input.selectionStart;
    const end = inputEl.current.input.selectionEnd;

    if (start === 0 && end === 0) {
      setInputCursorPosition({ start, end });
      return;
    }

    if (start === end) {
      const newValue = phoneNumber.slice(0, start - 1) + phoneNumber.slice(end);
      setPhoneNumber(newValue);
      setInputCursorPosition({ start: start - 1, end: end - 1 });
      return;
    }

    const newValue = phoneNumber.slice(0, start) + phoneNumber.slice(end);
    setPhoneNumber(newValue);
    setInputCursorPosition({ start, end: start });
  };

  const removeBtnRender = (
    <Button
      className="btn-after"
      data-testid=""
      onClick={removeKey}
      icon={<LeftSquareOutlined />}
    ></Button>
  );

  return (
    <div onKeyDown={handleEnterPress} className="dial-pad">
      {contextHolder}
      <div className="dial-pad__input">
        <Input
          addonBefore="+"
          autoFocus
          ref={inputEl}
          value={phoneNumber}
          placeholder={'Phone number'}
          addonAfter={removeBtnRender}
          maxLength={MAX_PHONE_LENGTH}
          data-testid="dial-pad-input"
          onChange={onChange}
        />
        {phoneNumber?.length === MAX_PHONE_LENGTH && (
          <div className="dial-pad__input-warning">
            The phone number is too long, please check it
          </div>
        )}
      </div>
      <KeyPad onKeyHandle={addChar} />
      <Button
        className="tw-size-12"
        data-testid="make-call-button"
        type="primary"
        shape="circle"
        onClick={() => makeCall(phoneNumber)}
        icon={<MessageIcon svgUrl={answerIcon} />}
      ></Button>
    </div>
  );
});
